import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../../lib/api';
import { initializePushNotifications } from '../../services/notifications';
import { forgotPassword } from '../../services/auth';

export function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      
      // Initialize push notifications after successful login
      try {
        await initializePushNotifications();
      } catch (pushError) {
        // Don't block login if push notification setup fails
        console.error('Push notification setup failed:', pushError);
      }
      
      navigate('/');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotStatus('loading');
    try {
      await forgotPassword(forgotEmail);
      setForgotStatus('done');
    } catch (err) {
      setForgotError(getErrorMessage(err));
      setForgotStatus('idle');
    }
  };

  if (showForgot) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Glemt adgangskode</h3>
        {forgotStatus === 'done' ? (
          <div className="space-y-4">
            <div className="p-3 bg-green-50 border border-green-400 rounded-lg">
              <p className="text-sm text-green-700">
                Hvis emailen er registreret, er der sendt et link til nulstilling af adgangskode. Tjek din indbakke.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setShowForgot(false); setForgotStatus('idle'); setForgotEmail(''); }}
              className="btn btn-primary w-full"
            >
              Tilbage til login
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotSubmit} className="space-y-4">
            <p className="text-sm text-gray-600">
              Indtast din email, og vi sender dig en midlertidig adgangskode.
            </p>
            <div>
              <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="forgot-email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="input"
                placeholder="din@email.dk"
              />
            </div>
            {forgotError && (
              <div className="p-3 bg-red-50 border border-red-400 rounded-lg">
                <p className="text-sm text-red-600">{forgotError}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setShowForgot(false); setForgotError(''); setForgotEmail(''); }}
                className="btn btn-secondary flex-1"
              >
                Annuller
              </button>
              <button
                type="submit"
                disabled={forgotStatus === 'loading'}
                className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {forgotStatus === 'loading' ? 'Sender...' : 'Send adgangskode'}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="input"
          placeholder="din@email.dk"
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Adgangskode
        </label>
        <input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="input"
          placeholder="••••••••"
        />
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-400 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Logger ind...' : 'Log ind'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => setShowForgot(true)}
          className="text-sm text-primary-green hover:underline"
        >
          Glemt adgangskode?
        </button>
      </div>
    </form>
  );
}
